// Companies House Watch — types mirroring the Supabase schema
// (supabase/migrations/20260729_companies_house_watch.sql) and the worker's
// settings contract (companies-house-watch repo, worker.py DEFAULT_SETTINGS /
// normalise_settings). Settings are v2: cadence + per-report signals + email
// style. Legacy v1 settings are migrated by settingsFromStored() below, the
// same mapping the worker applies on read.

export interface IcpSegment {
  label: string
  cap: number
  // bare SIC code -> human label
  sics: Record<string, string>
}

export interface IcpSpec {
  established_years: number
  priority_prefixes: string[]
  charge_lookup_cap: number
  geography?: { postcode_prefixes: string[] }
  groups: Record<string, IcpSegment>
}

export type ReportKind = 'digest' | 'leadgen'
export type Frequency = 'weekdays' | 'weekly' | 'paused'
export type EmailStyle = 'highlights' | 'full' | 'none'
export type EnrichSignal = 'triggers' | 'accounts' | 'changes' | 'risk'
export type LeadgenSignal = 'newinc' | EnrichSignal

export interface ChwSettings {
  cadence: { frequency: Frequency; day: string; hour: string }
  enrich: { enabled: boolean; signals: EnrichSignal[] }
  leadgen: { enabled: boolean; signals: LeadgenSignal[] }
  icp_spec: IcpSpec | null // null = worker's built-in default ICP
  email: { style: EmailStyle; admin_emails: string[]; bcc: string[]; owners: boolean }
  first_window_days: number
}

export interface ChwOrg {
  id: string
  org_key: string
  display_name: string
  is_active: boolean
  settings: Record<string, unknown> | null
  created_at?: string
}

export interface ChwReportMeta {
  id: string
  kind: ReportKind
  report_date: string
  window_since: string | null
  subject: string
  created_at: string
}

// The worker's report JSON (chw_reports.data): enough structure for the
// native report view. Filings rows come straight from the sweep CSV.
export interface ChwReportEntry {
  note: string
  filings: Array<Record<string, string>>
  company: string | { name?: string; locality?: string; region?: string;
    postcode?: string; created?: string; number?: string; group?: string;
    in_crm?: boolean }
  owner?: string
  lender?: string
}
export interface ChwReportData {
  subject?: string
  since?: string
  window_since?: string
  swept?: number
  counts?: Record<string, number>
  sections?: Record<string, Record<string, ChwReportEntry>>
  new_incorporations?: Array<{ number: string; name: string; created: string;
    locality?: string; region?: string; postcode?: string; in_crm?: boolean }>
}

// Portal-requested actions the worker executes (chw_actions).
export interface ChwLeadPayload {
  number: string
  name: string
  locality?: string
  region?: string
  postcode?: string
  created?: string
  signal?: string
  note?: string
  lender?: string
}
export interface ChwAction {
  id: string
  org_id: string
  type: 'create_lead'
  payload: ChwLeadPayload
  status: 'requested' | 'done' | 'failed'
  result: string | null       // created | already_exists
  hs_company_id: string | null
  error: string | null
  created_at: string
  finished_at: string | null
}

export interface ChwRun {
  id: string
  org_id: string
  kind: string
  status: 'requested' | 'running' | 'done' | 'failed'
  created_at: string
  started_at: string | null
  finished_at: string | null
  error: string | null
  summary: Record<string, unknown> | null
}

export const ENRICH_SIGNALS: EnrichSignal[] = ['triggers', 'accounts', 'changes', 'risk']
export const LEADGEN_SIGNALS: LeadgenSignal[] = ['newinc', 'triggers', 'accounts', 'changes', 'risk']

export const DEFAULT_SETTINGS: ChwSettings = {
  cadence: { frequency: 'weekdays', day: 'MON', hour: '07:00' },
  enrich: { enabled: true, signals: [...ENRICH_SIGNALS] },
  leadgen: { enabled: false, signals: [...LEADGEN_SIGNALS] },
  icp_spec: null,
  email: { style: 'highlights', admin_emails: [], bcc: [], owners: false },
  first_window_days: 7,
}

/** Read whatever is stored in chw_orgs.settings into a full v2 ChwSettings.
 * Mirrors worker.py normalise_settings(): fresh orgs get the v2 defaults,
 * v1 shapes (days/reports/schedule_days/email_mode) are migrated. */
export function settingsFromStored(raw: Record<string, unknown> | null): ChwSettings {
  const d = DEFAULT_SETTINGS
  const clone: ChwSettings = JSON.parse(JSON.stringify(d))
  if (!raw || Object.keys(raw).length === 0) return clone
  if (raw.cadence || raw.enrich || raw.leadgen) {
    const r = raw as Partial<ChwSettings>
    return {
      cadence: { ...clone.cadence, ...(r.cadence || {}) },
      enrich: { ...clone.enrich, ...(r.enrich || {}) },
      leadgen: { ...clone.leadgen, ...(r.leadgen || {}) },
      icp_spec: (r.icp_spec as IcpSpec | null) ?? null,
      email: { ...clone.email, ...(r.email || {}) },
      first_window_days: Number(r.first_window_days) || 7,
    }
  }
  // legacy v1
  const reports = (raw.reports as string[]) || ['digest']
  const days = ((raw.schedule_days as string[]) || ['MON']).map(x => x.toUpperCase())
  const mode = (raw.email_mode as string) || 'admin'
  return {
    cadence: days.length >= 5
      ? { frequency: 'weekdays', day: 'MON', hour: '07:00' }
      : { frequency: 'weekly', day: days[0] || 'MON', hour: '07:00' },
    enrich: { enabled: reports.includes('digest'), signals: [...ENRICH_SIGNALS] },
    leadgen: { enabled: reports.includes('leadgen'), signals: [...LEADGEN_SIGNALS] },
    icp_spec: (raw.icp_spec as IcpSpec | null) ?? null,
    email: {
      style: mode === 'none' ? 'none' : 'full',
      admin_emails: (raw.admin_emails as string[]) || [],
      bcc: [],
      owners: mode === 'owners',
    },
    first_window_days: Number(raw.days) || 7,
  }
}

// Shown when an org has no icp_spec yet and enables lead-gen: the worker's
// built-in asset-finance ICP (keep in sync with leadgen.py DEFAULT_SPEC).
export const DEFAULT_ICP_SPEC: IcpSpec = {
  established_years: 2,
  priority_prefixes: ['NI', 'R0'],
  charge_lookup_cap: 40,
  groups: {
    seg1: {
      label: 'Plant hire & construction contractors', cap: 1100, sics: {
        '77320': 'Plant hire (construction machinery rental)',
        '43120': 'Site preparation / groundworks',
        '43110': 'Demolition',
        '42990': 'Civil engineering n.e.c.',
        '42110': 'Roads & motorways construction',
        '43991': 'Scaffold erection',
        '08110': 'Quarrying (building stone, limestone)',
        '08120': 'Sand & gravel pits',
        '23610': 'Concrete products manufacture',
        '23630': 'Ready-mixed concrete',
      },
    },
    seg2: {
      label: 'Transport, haulage & fleet operators', cap: 700, sics: {
        '49410': 'Freight transport by road',
        '49420': 'Removal services',
        '52290': 'Other transportation support',
        '77120': 'Truck rental & leasing',
      },
    },
    seg3: {
      label: 'Precision engineering & niche manufacturers', cap: 500, sics: {
        '25620': 'Machining (CNC)',
        '25730': 'Tool manufacture',
        '28290': 'General-purpose machinery manufacture',
        '28990': 'Special-purpose machinery n.e.c.',
        '22220': 'Plastic packaging manufacture',
      },
    },
    seg4: {
      label: 'Agri & landscaping contractors (secondary)', cap: 300, sics: {
        '01610': 'Agricultural contracting (crop support)',
        '77310': 'Agricultural machinery rental',
        '81300': 'Landscaping services',
      },
    },
  },
}
